import { useEffect, useRef, useState } from 'react';

type State = { phase: 'loading' | 'playing' | 'error'; message?: string };

/**
 * Plays a stream in the browser as it leaves the origin (clear DASH or HLS only), muted.
 * The player libraries load only when this is shown.
 */
export function TestPlayer({ url, type }: { url: string; type: 'dash' | 'hls' }) {
  const video = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [stats, setStats] = useState<{ height: number; buffer: number } | null>(null);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    let destroy: (() => void) | undefined;
    let cancelled = false;
    setState({ phase: 'loading' });
    setStats(null);
    const fail = (message: string) => !cancelled && setState({ phase: 'error', message });

    (async () => {
      try {
        if (type === 'dash') {
          const dashjs = await import('dashjs');
          if (cancelled) return;
          const player = dashjs.MediaPlayer().create();
          player.initialize(el, url, true);
          player.on(dashjs.MediaPlayer.events.ERROR, (e: { error?: { message?: string } }) => fail(e.error?.message ?? 'The player could not play this stream'));
          destroy = () => player.destroy();
        } else {
          const Hls = (await import('hls.js')).default;
          if (cancelled) return;
          if (Hls.isSupported()) {
            const hls = new Hls({ lowLatencyMode: true });
            hls.on(Hls.Events.ERROR, (_e, data) => data.fatal && fail(`${data.type}: ${data.details}`));
            hls.loadSource(url);
            hls.attachMedia(el);
            destroy = () => hls.destroy();
          } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
            el.src = url;
            destroy = () => el.removeAttribute('src');
          } else fail('This browser cannot play HLS');
        }
      } catch (err) {
        fail((err as Error).message);
      }
    })();

    const onPlaying = () => !cancelled && setState({ phase: 'playing' });
    el.addEventListener('playing', onPlaying);
    const timer = setInterval(() => {
      if (!el.buffered.length) return;
      setStats({ height: el.videoHeight, buffer: Math.max(0, el.buffered.end(el.buffered.length - 1) - el.currentTime) });
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      el.removeEventListener('playing', onPlaying);
      destroy?.();
    };
  }, [url, type]);

  return (
    <div className="mc-player">
      <video ref={video} className="mc-player__video" muted autoPlay playsInline controls aria-label="Test player" />
      <div className="mc-player__bar mc-small">
        <span className={state.phase === 'error' ? 'mc-bad' : state.phase === 'playing' ? 'mc-good' : 'hv-muted'}>
          {state.phase === 'loading' ? 'Loading…' : state.phase === 'playing' ? 'Playing' : state.message}
        </span>
        {stats && state.phase === 'playing' && (
          <span className="hv-muted">
            {stats.height ? `${stats.height}p` : ''} · {stats.buffer.toFixed(1)} s buffered
          </span>
        )}
      </div>
    </div>
  );
}
